import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import CallIcon from "@assets/images/call-icon.svg?react";
import LocationIcon from "@assets/images/location-icon.svg?react";
import RightArrowIcon from "@assets/images/right-arrow.svg?react";

import SearchBox from "@components/SearchBox";

import { useGetMaps } from "@api/maps";

import type { ResponseGetMaps } from "@schemas/maps";

import { waitForNaverMaps } from "@utils/naverMapsLoader";

import useMediaQuery from "@hooks/useMediaQuery";

import { DEFAULT_PAGE_SIZE } from "@/config";

import "./style.scss";
import PathContants from "@/routers/pathConstants";

interface SearchPartnerProps {
	noMap?:
		| {
				mobile?: boolean;
				desktop?: boolean;
		  }
		| boolean;
}

const SearchPartner = ({ noMap }: SearchPartnerProps) => {
	const [search, setSearch] = useState("");

	const mapRef = useRef<naver.maps.Map | null>(null);
	const markersMapRef = useRef<Record<number, naver.maps.Marker>>({});
	// 클러스터 제거로 더 이상 사용하지 않음
	const [page, setPage] = useState(0);
	const [items, setItems] = useState<ResponseGetMaps[]>([]);
	const [hasMore, setHasMore] = useState(true);
	const [mapReady, setMapReady] = useState(false);
	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const { data: maps, isFetching } = useGetMaps({ query: search, size: DEFAULT_PAGE_SIZE, page });
	const navigate = useNavigate();
	const location = useLocation();
	const stateData = (location?.state as { searchData: ResponseGetMaps }) ?? {};
	const totalCount = maps?.data.total ?? items.length;

	useEffect(() => {
		// 검색어가 바뀌면 페이지와 아이템 초기화
		setPage(0);
		setItems([]);
		setHasMore(true);
	}, [search]);

	useEffect(() => {
		if (!maps) return;
		const newList = maps.data.list ?? [];
		setItems((prev) => (page === 0 ? newList : [...prev, ...newList]));
		const total = maps.data.total ?? 0;
		const accumulated = (page + 1) * DEFAULT_PAGE_SIZE;
		setHasMore(accumulated < total);
	}, [maps, page]);
	const isMobile = useMediaQuery("(max-width: 768px)");

	const isNoMap =
		noMap === true ||
		(noMap && noMap.mobile && isMobile) ||
		(noMap && noMap.desktop && !isMobile);
	useEffect(() => {
		const el = sentinelRef.current;
		if (!el) return;
		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry.isIntersecting && hasMore && !isFetching) {
					setPage((p) => p + 1);
				}
			},
			{ root: null, rootMargin: "0px", threshold: 1.0 },
		);
		observer.observe(el);
		return () => {
			observer.disconnect();
		};
	}, [hasMore, isFetching]);

	const handleSearch = (value: string) => {
		setSearch(value);
	};

	const handleMapClick = (item: ResponseGetMaps) => {
		if (isNoMap) {
			navigate(PathContants.Partner, {
				state: {
					searchData: item,
				},
			});
			return;
		}
		if (!mapRef.current) return;
		const position = new window.naver.maps.LatLng(item.posY, item.posX);
		mapRef.current.setCenter(position);
		mapRef.current.setZoom(15);
	};

	useEffect(() => {
		let isMounted = true;
		(async () => {
			try {
				const maps = await waitForNaverMaps();
				if (!isMounted) return;
				const map = document.getElementById("map");
				if (map) {
					mapRef.current = new maps.Map(map, {
						center: new maps.LatLng(37.566826, 126.9786567),
						zoom: 10,
					});
					setMapReady(true);
					// 지도 로드 완료 후 stateData가 있으면 해당 값으로 검색 및 이동
					const target = stateData?.searchData as ResponseGetMaps | undefined;
					if (target) {
						setSearch(target.companyName || "");
						try {
							const position = new maps.LatLng(target.posY, target.posX);
							mapRef.current.setCenter(position);
							mapRef.current.setZoom(15);
						} catch (err) {
							console.warn("Failed to center map with stateData", err);
						}
					}
				}
			} catch (e) {
				console.error(e);
			}
		})();

		return () => {
			isMounted = false;
			if (mapRef.current) {
				mapRef.current.destroy();
			}
			setMapReady(false);
			// clear markers
			Object.values(markersMapRef.current).forEach((mk) => mk.setMap(null));
			markersMapRef.current = {};
			// 클러스터 제거 로직 삭제
		};
	}, []);

	// create markers for current items
	useEffect(() => {
		if (!mapReady || !mapRef.current || isNoMap) return;
		// clear previous markers
		Object.values(markersMapRef.current).forEach((mk) => mk.setMap(null));
		markersMapRef.current = {};

		const createdMarkers: naver.maps.Marker[] = [];

		items.forEach((item) => {
			const position = new window.naver.maps.LatLng(item.posY, item.posX);
			const content = `
                <div  style="display:inline-flex;flex-direction:column;align-items:center;">
                    <div  style="width:24px;height:24px;background:url('/images/marker.png') no-repeat center / contain;"></div>
                    <div  style="margin-top:4px;color:#1a1a1a;font-size:12px;font-weight:600;white-space:nowrap;">${item.companyName}</div>
                </div>
            `;
			const marker = new window.naver.maps.Marker({
				position,
				icon: {
					content,
					size: new window.naver.maps.Size(24, 24),
					scaledSize: new window.naver.maps.Size(24, 24),
					anchor: new window.naver.maps.Point(12, 24),
					origin: new window.naver.maps.Point(0, 0),
				} as any,
				title: item.companyName,
			});
			window.naver.maps.Event.addListener(marker, "click", () => handleMapClick(item));
			markersMapRef.current[item.id] = marker;
			createdMarkers.push(marker);
		});

		// 클러스터 사용 제거: 생성된 마커를 지도에 직접 표시
		createdMarkers.forEach((m) => m.setMap(mapRef.current!));
	}, [items, mapReady, isNoMap]);

	useEffect(() => {
		if (!mapRef.current || !("geolocation" in navigator)) return;
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const { latitude, longitude } = pos.coords;
				const position = new window.naver.maps.LatLng(latitude, longitude);
				mapRef.current!.setCenter(position);
				mapRef.current!.setZoom(13);
			},
			() => {},
			{ enableHighAccuracy: true, timeout: 10000 },
		);
	}, []);
	return (
		<div className="search-partner-container">
			<div className="search-partner-panel">
				{!isMobile && (
					<div className="search-box-wrapper desktop">
						<SearchBox
							placeholder="대리점 검색"
							value={search}
							onChange={handleSearch}
						/>
						<span className="search-box-count">
							총 {totalCount}개의 검색결과가 있습니다.
						</span>
					</div>
				)}
				<div className="search-result">
					{items.map((item) => (
						<div
							className="search-result__item"
							key={item.id}
							onClick={() => handleMapClick(item)}
						>
							<div className="search-result__item__title-wrapper">
								<strong>{item.companyName}</strong>
								<RightArrowIcon className="right-arrow-icon" />
							</div>
							<div className="search-result__item__info-wrapper">
								<div className="info-item">
									<LocationIcon className="location-icon" />
									<span>{item.address}</span>
								</div>
								<div className="info-item">
									<CallIcon className="call-icon" />
									<span>{item.phone}</span>
								</div>
							</div>
						</div>
					))}
					<div ref={sentinelRef} />
				</div>
			</div>
			{!isNoMap && (
				<div className="search-partner-map-content">
					<div id="map" />
				</div>
			)}
			{isMobile && (
				<div className="search-box-wrapper mobile">
					<SearchBox placeholder="대리점 검색" value={search} onChange={handleSearch} />
				</div>
			)}
		</div>
	);
};

export default SearchPartner;
